import type { CreateEmployeeInvoiceData, UpdateEmployeeInvoiceData } from "@/interfaces";
import { DEFAULT_CACHE_TIME } from "@/utils/constants";
import { useQuery } from "@tanstack/react-query";
import api from "./api";

// ============================================================================
// INVOICE TYPES FOR SERVICE LAYER
// ============================================================================

export type {
  CreateEmployeeInvoiceData,
  EmployeeInvoiceType,
  EmployeeInvoiceItemType as InvoiceItemType,
  UpdateEmployeeInvoiceData,
} from "@/interfaces";

export interface GetInvoicesParams {
  page?: number;
  limit?: number;
  status?: string;
  clientName?: string;
  createdDateFrom?: string;
  createdDateTo?: string;
  search?: string;
}

/**
 * React Query hook to fetch invoice by ID
 */
export const useInvoices = (companyId?: string, enabled = true) => {
  return useQuery({
    queryKey: ["quickbooks", "invoices", companyId],
    queryFn: async () => {
      const response = await api.get("quickbooks/invoices", {
        params: { companyId },
      });
      return response.data;
    },
    enabled: enabled && !!companyId,
    staleTime: DEFAULT_CACHE_TIME,
  });
};

export const getQbInvoiceById = async (invoiceId: string, companyId: string) => {
  const response = await api.get(`/quickbooks/invoice?companyId=${companyId}&invoiceId=${invoiceId}`);
  return response.data;
};

/**
 * React Query hook to fetch invoice by ID
 */
export const useInvoiceById = (id: string, enabled = true) => {
  return useQuery({
    queryKey: ["invoice", id],
    queryFn: async () => {
      const response = await api.get(`/invoices/${id}`);
      return response.data;
    },
    enabled: enabled && !!id,
    staleTime: DEFAULT_CACHE_TIME,
  });
};

/**
 * React Query hook to fetch QuickBooks invoices for a specific client
 * @param clientId - Client UUID (optional, will use authenticated user's clientId on backend if not provided)
 * @param companyId - Company UUID (required)
 * @param enabled - Whether to enable the query
 */
export const useClientInvoices = (
  clientId?: string,
  companyId?: string,
  enabled = true,
  filters?: {
    startDate?: string;
    endDate?: string;
  },
) => {
  return useQuery({
    queryKey: ["client-invoices", clientId, companyId, filters?.startDate, filters?.endDate],
    queryFn: async () => {
      const params: {
        companyId: string;
        clientId?: string;
        startDate?: string;
        endDate?: string;
      } = {
        companyId: companyId!,
        startDate: filters?.startDate,
        endDate: filters?.endDate,
      };
      if (clientId) {
        params.clientId = clientId;
      }
      const response = await api.get("/quickbooks/client-invoices", { params });
      return response.data;
    },
    enabled: enabled && !!companyId,
    staleTime: DEFAULT_CACHE_TIME,
  });
};

/**
 * React Query hook to fetch QuickBooks recurring transactions
 * @param companyId - Company UUID (optional)
 * @param clientId - Client UUID (optional)
 * @param enabled - Whether to enable the query
 */
export const useRecurringTransactions = (companyId?: string, clientId?: string, enabled = true) => {
  return useQuery({
    queryKey: ["recurring-transactions", companyId, clientId],
    queryFn: async () => {
      const response = await api.get("/quickbooks/recurring-transactions", {
        params: { companyId, clientId },
      });
      return response.data;
    },
    enabled: enabled && (!!companyId || !!clientId),
    staleTime: DEFAULT_CACHE_TIME,
  });
};

export const quickBooksInvoiceService = {
  /**
   * Create a new invoice
   */

  createInvoice: async (companyId: string, data: CreateEmployeeInvoiceData) => {
    // Transform internal invoice shape into QuickBooks invoice payload expected by the server
    const qbLineItems = (data.items || []).map((item) => ({
      DetailType: "SalesItemLineDetail",
      Amount: item.amount,
      SalesItemLineDetail: {
        ItemRef: {
          name: item.productService || "",
          value: item.productService || "",
        },
        Qty: item.quantity,
        UnitPrice: item.rate,
      },
      Description: item.description,
    }));

    const qbInvoicePayload: any = {
      Line: qbLineItems,
      CustomerRef: {
        value: data.clientId,
      },
      TxnDate: data.issueDate || undefined,
      DueDate: data.dueDate || undefined,
      CustomerMemo: {
        value: data.notes || "",
      },
      PrivateNote: `Sale Type: ${data.saleType || "N/A"} | Service Period: ${data.serviceStartDate || ""} to ${
        data.serviceEndDate || ""
      }${data.vendorBillLink ? ` | Vendor Bill: ${data.vendorBillLink}` : ""}`,
    };

    if (data.recurringInvoice) {
      qbInvoicePayload.RecurringInfo = {
        Name: data.recurringInvoice.templateName,
        RecurType: data.recurringInvoice.recurType,
        ScheduleInfo: {
          IntervalType: data.recurringInvoice.intervalType,
          NumInterval: data.recurringInvoice.numInterval,
          DayOfMonth: data.recurringInvoice.dayOfMonth,
          StartDate: data.recurringInvoice.startDate,
          EndDate: data.recurringInvoice.endDate,
          MaxOccurrences: data.recurringInvoice.maxOccurrences,
          DaysBefore: data.recurringInvoice.daysBefore,
        },
      };
    }

    const response = await api.post(`/quickbooks/create?companyId=${companyId}`, qbInvoicePayload);
    return response.data;
  },

  getRecurringTransactions: async (companyId?: string, clientId?: string) => {
    const response = await api.get("/quickbooks/recurring-transactions", {
      params: { companyId, clientId },
    });
    return response.data;
  },

  deleteRecurringTransaction: async (companyId: string, recurringTransactionId: string) => {
    const response = await api.delete("/quickbooks/recurring-transaction", {
      params: { companyId, recurringTransactionId },
    });
    return response.data;
  },

  /**
   * Update an existing invoice
   */
  updateInvoice: async (id: string, data: UpdateEmployeeInvoiceData) => {
    const response = await api.put(`/quickbooks/invoices/${id}`, data);
    return response.data;
  },
};
